<template>
  <BaseModal 
    :is-visible="isVisible" 
    title="Enter Subject ID and Dataset ID"
    @close="handleClose"
    @confirm="handleConfirm"
  >
    <template #content>
      <div class="space-y-4">
        <p class="text-sm text-gray-600">
          The filename <strong>{{ filename }}</strong> doesn't match the expected format: <strong>&lt;subid&gt;_&lt;dataset_id&gt;</strong>
        </p>
        <p class="text-sm text-gray-600">
          Please enter the Subject ID and Dataset ID manually:
        </p>
        
        <div class="space-y-4">
          <div class="space-y-2">
            <label for="subid-input" class="block text-sm font-medium text-gray-700">
              Subject ID
            </label>
            <input
              id="subid-input"
              v-model="subidInput"
              type="text"
              placeholder="Enter 3-6 digit Subject ID"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              @keyup.enter="handleConfirm"
              ref="subidInput"
            />
          </div>

          <div class="space-y-2">
            <label for="dataset-id-input" class="block text-sm font-medium text-gray-700">
              Dataset ID
            </label>
            <input
              id="dataset-id-input"
              v-model="datasetIdInput"
              type="text"
              placeholder="Enter Dataset ID (e.g., 001, 123)"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              @keyup.enter="handleConfirm"
            />

          </div>
        </div>

        <div v-if="validationError" class="p-3 text-sm text-red-700 bg-red-100 rounded-md">
          {{ validationError }}
        </div>
      </div>
    </template>

    <template #footer>
      <button
        @click="handleClose"
        class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
      >
        Cancel
      </button>
      <button
        @click="handleConfirm"
        :disabled="!isValidInput"
        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Confirm
      </button>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from './BaseModal.vue'
import { isSubidValid } from '../utils/dataUploadConfiguration.js'

// Helper function to validate Dataset ID (must be exactly 3 digits)
function isDatasetIdValid(datasetId) {
  return /^\d{3}$/.test(datasetId);
}

export default {
  name: 'SubIdDatasetModal',
  components: {
    BaseModal
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    filename: {
      type: String,
      required: true
    }
  },
  emits: ['close', 'confirm'],
  data() {
    return {
      subidInput: '',
      datasetIdInput: '',
      validationError: ''
    }
  },
  computed: {
    isValidInput() {
      return isSubidValid(this.subidInput) && isDatasetIdValid(this.datasetIdInput);
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.$refs.subidInput?.focus();
        });
      } else {
        this.resetForm();
      }
    },
    subidInput() {
      this.validationError = '';
      if (this.subidInput && !isSubidValid(this.subidInput)) {
        this.validationError = 'Subject ID must be 3-6 digits';
      }
    },
    datasetIdInput() {
      this.validationError = '';
      if (this.datasetIdInput && !isDatasetIdValid(this.datasetIdInput)) {
        this.validationError = 'Dataset ID must be exactly 3 digits (e.g., 001, 123)';
      }
    }
  },
  methods: {
    handleClose() {
      this.resetForm();
      this.$emit('close');
    },
    handleConfirm() {
      if (!this.isValidInput) {
        if (!isSubidValid(this.subidInput)) {
          this.validationError = 'Subject ID must be 3-6 digits';
        } else if (!isDatasetIdValid(this.datasetIdInput)) {
          this.validationError = 'Dataset ID must be exactly 3 digits';
        } else {
          this.validationError = 'Please enter both a valid Subject ID and Dataset ID';
        }
        return;
      }
      
      this.$emit('confirm', {
        subid: this.subidInput,
        datasetId: this.datasetIdInput.trim()
      });
      this.resetForm();
    },
    resetForm() {
      this.subidInput = '';
      this.datasetIdInput = '';
      this.validationError = '';
    }
  }
}
</script>
